import { isRecoveryRoute } from './isRecoveryRoute';
import { PwaUpdateContextValue } from './types';
import { useCallback, useMemo, useState } from 'react';
import { useRegisterSW } from 'virtual:pwa-register/react';

const getPwaUpdateError = (value: unknown): Error => {
  if (value instanceof Error) {
    return value;
  }

  return new Error('Failed to handle application update');
};

export const usePwaUpdate = (isReloadDeferred: boolean): PwaUpdateContextValue => {
  const [error, setError] = useState<Error | null>(null);
  const [isReloadRequired, setIsReloadRequired] = useState(false);
  const [registration, setRegistration] = useState<ServiceWorkerRegistration | null>(null);
  const [swScriptUrl, setSwScriptUrl] = useState<string | null>(null);

  const {
    needRefresh: [isNeedRefresh, setIsNeedRefresh],
    offlineReady: [isOfflineReady],
    updateServiceWorker
  } = useRegisterSW({
    immediate: true,
    onNeedRefresh() {
      setIsNeedRefresh(true);
    },
    onNeedReload() {
      setIsReloadRequired(true);
    },
    onRegisteredSW(registeredSwScriptUrl, serviceWorkerRegistration) {
      setRegistration(serviceWorkerRegistration ?? null);
      setSwScriptUrl(registeredSwScriptUrl);

      void serviceWorkerRegistration?.update().catch((updateError: unknown) => {
        setError(getPwaUpdateError(updateError));
      });
    },
    onRegisterError(registerError: unknown) {
      setError(getPwaUpdateError(registerError));
    }
  });

  const activateUpdate = useCallback(async () => {
    if (isReloadDeferred || isRecoveryRoute(window.location.pathname)) return;
    setError(null);

    try {
      await updateServiceWorker(true);
      if (!isRecoveryRoute(window.location.pathname)) window.location.reload();
    } catch (updateError) {
      setError(getPwaUpdateError(updateError));
      setIsReloadRequired(true);
      throw updateError;
    }
  }, [isReloadDeferred, updateServiceWorker]);

  const checkForUpdate = useCallback(async () => {
    if (!registration) {
      return;
    }

    setError(null);

    try {
      await registration.update();
    } catch (updateError) {
      setError(getPwaUpdateError(updateError));
    }
  }, [registration]);

  return useMemo(
    () => ({
      activateUpdate,
      checkForUpdate,
      error,
      isOfflineReady,
      isReloadDeferred,
      isUpdateAvailable: isNeedRefresh || isReloadRequired,
      registration,
      swScriptUrl
    }),
    [
      activateUpdate,
      checkForUpdate,
      error,
      isNeedRefresh,
      isOfflineReady,
      isReloadDeferred,
      isReloadRequired,
      registration,
      swScriptUrl
    ]
  );
};